import { getApiUrl } from "../config";

export interface BlockData {
  height: number;
  timestamp: string;
  transactions: number;
  size: number;
  weight: number;
  totalFees: number;
  avgFeePerVByte: number;
}

export interface TransactionData {
  hash: string;
  bitcoinValue: number;
  size: number;
  fee: number;
  satFeePerVByte: number;
}

export interface BlocksResponse {
  success: boolean;
  data: BlockData[];
  message?: string;
  error?: string;
}

export interface TransactionsResponse {
  success: boolean;
  data: TransactionData[];
  message?: string;
  error?: string;
}

export interface BitcoinPriceData {
  price: number;
  change24h: number;
}

export interface BitcoinPriceResponse {
  success: boolean;
  data: BitcoinPriceData;
  message?: string;
  error?: string;
}

export interface HashRateData {
  hashRate: number;
  unit: string;
  timestamp: number;
}

export interface HashRateResponse {
  success: boolean;
  data: HashRateData;
  message?: string;
  error?: string;
}

export const fetchLatestBlocks = async (): Promise<BlockData[]> => {
  try {
    const res = await fetch(getApiUrl("/api/blocks/latest"));
    
    if (!res.ok) {
      throw new Error(`Failed to fetch blocks: ${res.status} ${res.statusText}`);
    }
    
    const response: BlocksResponse = await res.json();
    
    if (!response.success) {
      throw new Error(response.message || response.error || 'Failed to fetch blocks');
    }
    
    return response.data;
  } catch (error) {
    console.error('Error fetching latest blocks:', error);
    throw error;
  }
};

export const fetchLatestTransactions = async (): Promise<TransactionData[]> => {
  try {
    const res = await fetch(getApiUrl("/api/transactions/latest"));
    
    if (!res.ok) {
      throw new Error(`Failed to fetch transactions: ${res.status} ${res.statusText}`);
    }
    
    const response: TransactionsResponse = await res.json();
    
    if (!response.success) {
      throw new Error(response.message || response.error || 'Failed to fetch transactions');
    }
    
    return response.data;
  } catch (error) {
    console.error('Error fetching latest transactions:', error);
    throw error;
  }
};

export const fetchBitcoinPrice = async (): Promise<BitcoinPriceData> => {
  try {
    const res = await fetch(getApiUrl("/api/bitcoin/price"));
    
    if (!res.ok) {
      throw new Error(`Failed to fetch Bitcoin price: ${res.status} ${res.statusText}`);
    }
    
    const response: BitcoinPriceResponse = await res.json();
    
    if (!response.success) {
      throw new Error(response.message || response.error || 'Failed to fetch Bitcoin price');
    }
    
    return response.data;
  } catch (error) {
    console.error('Error fetching Bitcoin price:', error);
    throw error;
  }
};

export const fetchHashRate = async (): Promise<HashRateData> => {
  try {
    const res = await fetch(getApiUrl("/api/bitcoin/hashrate"));
    
    if (!res.ok) {
      throw new Error(`Failed to fetch hash rate: ${res.status} ${res.statusText}`);
    }
    
    const response: HashRateResponse = await res.json();
    
    if (!response.success) {
      throw new Error(response.message || response.error || 'Failed to fetch hash rate');
    }
    
    return response.data;
  } catch (error) {
    console.error('Error fetching hash rate:', error);
    throw error;
  }
};